import {Component, EventEmitter, Input, OnDestroy, OnInit, Optional, Output, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {RouterModule} from '@angular/router';
import {SharedModule} from 'primeng/api';
import {ButtonModule} from 'primeng/button';
import {DrawerModule} from 'primeng/drawer';
import {PopoverModule} from 'primeng/popover';
import {SelectModule} from 'primeng/select';
import {TooltipModule} from 'primeng/tooltip';
import {IconoPrimePipe} from '@app/pipe/icono-prime.pipe';
import {Select, Store} from '@ngxs/store';
import {Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {Popover} from 'primeng/popover';
import {SesionState} from '@app/state/sesion/sesion.state';
import {SetCompanySelected, SetOffice, SetSaleStation} from '@app/state/sesion/sesion.actions';
import {Company} from '@app/domain/company';
import {Office} from '@app/domain/office';
import {SaleStation} from '@app/domain/sale-station';
import {ShortcutMenu} from '@app/domain/shortcut-menu';
import {LoginService} from '@app/service/login.service';
import {SaleStationService} from '@app/service/sale-station.service';
import {CoordinadorSalidaVentaService} from '@app/service/coordinador-salida-venta.service';
import {MARCA} from '@app/config/plantilla';

@Component({
    selector: 'app-context-switcher',
    templateUrl: './context-switcher.component.html',
    styleUrls: ['./context-switcher.component.scss'],
    // Standalone para usarse también en la cabecera del POS rápido (modo mostrador).
    imports: [CommonModule, FormsModule, RouterModule, SharedModule, ButtonModule, DrawerModule, PopoverModule, SelectModule,
        TooltipModule, IconoPrimePipe]
})
export class ContextSwitcherComponent implements OnInit, OnDestroy {
    readonly marca = MARCA;
    private unsubscribe$ = new Subject<void>();

    @Select(SesionState.getCompany) company$: Observable<Company>;
    @Select(SesionState.getCompanySelected) companySelected$: Observable<Company>;
    @Select(SesionState.getOffice) office$: Observable<Office>;
    @Select(SesionState.getSaleStation) saleStation$: Observable<SaleStation>;

    // valores aplicados (los que muestra el resumen del header)
    company: Company;
    companySelected: Company;
    office: Office;
    saleStation: SaleStation;

    // valores de trabajo dentro del panel; se aplican solo al confirmar
    empresaSel: Company;
    officeSel: Office;
    stationSel: SaleStation;

    attachedCompanies: Company[] = [];
    offices: Office[] = this.loginService.getPuntosVentaUsuario();
    stations: SaleStation[] = [];
    loadingStations = false;
    private stationsForOfficeId: any = null;

    panelOpen = false;
    sheetVisible = false;

    // selector rápido por segmento (empresa / sede / estación)
    segMode: 'empresa' | 'sede' | 'estacion' = 'sede';

    // secciones extra del bottom sheet en móvil
    @Input() shortcuts: ShortcutMenu[] = [];
    @Input() contextoPos = false;
    @Input() printerVisible = false;
    @Input() printerConnected = false;
    @Output() printerClick = new EventEmitter<void>();
    @Input() whatsappVisible = false;
    @Input() whatsappConnected = false;
    @Input() whatsappRoute: string;

    @ViewChild('panel') panel: Popover;
    @ViewChild('segPanelEmpresa') segPanelEmpresa: Popover;
    @ViewChild('segPanelSede') segPanelSede: Popover;
    @ViewChild('segPanelEstacion') segPanelEstacion: Popover;

    constructor(private loginService: LoginService,
                private saleStationService: SaleStationService,
                private store: Store,
                @Optional() private coordinador?: CoordinadorSalidaVentaService) { }

    /**
     * Antes de despachar un cambio de contexto, el editor de venta activo (POS) guarda su trabajo bajo el contexto
     * actual. Si no puede, se cancela el cambio y se restauran los valores mostrados.
     */
    private async confirmarCambio(): Promise<boolean> {
        if (!this.coordinador?.activo) { return true; }
        if (this.confirmando) { return false; }
        this.confirmando = true;
        try {
            const permitido = await this.coordinador.solicitarSalida('contexto');
            if (!permitido) {
                this.syncWorkingValues();
                this.hideSegPanels();
                this.close();
            }
            return permitido;
        } finally {
            this.confirmando = false;
        }
    }

    /** Mientras el editor guarda no se aceptan otros cambios de contexto. */
    confirmando = false;
    private consultaEstaciones = 0;

    ngOnInit(): void {
        this.company$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(c => {
                this.company = c;
                const user = this.loginService.getUser();
                this.attachedCompanies = user ? [c, ...(user.empresasAdjuntas || [])].filter(Boolean) : [];
            });
        this.companySelected$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(c => this.companySelected = c);
        this.office$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(o => this.office = o);
        this.saleStation$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(s => this.saleStation = s);
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    get canSelectCompany() {
        return this.loginService.hasPermission('SELECCIONAR_EMPRESAS_ADJUNTAS') && this.attachedCompanies.length > 1;
    }

    get isMobile() {
        return window.matchMedia('(max-width: 991px)').matches;
    }

    open(event: Event) {
        event.preventDefault();
        this.hideSegPanels();
        this.initWorkingValues();
        if (this.isMobile) {
            this.sheetVisible = true;
        } else {
            this.panel.toggle(event);
            this.panelOpen = true;
        }
    }

    // --- selector rápido: clic directo sobre un segmento del resumen ---

    openSegment(seg: 'empresa' | 'sede' | 'estacion', event: Event) {
        event.preventDefault();
        // que el click no llegue al listener de documento de los overlays
        event.stopPropagation();
        if (this.isMobile) {
            // en móvil el chip abre el sheet completo
            this.open(event);
            return;
        }
        if (seg === 'empresa' && !this.canSelectCompany) {
            return;
        }
        if (this.panel) {
            this.panel.hide();
            this.panelOpen = false;
        }
        this.hideSegPanels(seg);
        this.syncWorkingValues();
        this.segMode = seg;
        if (seg === 'estacion' && this.stationsForOfficeId !== this.office?.id) {
            this.loadStations(this.office, this.saleStation, false);
        }
        this.segPanelFor(seg)?.toggle(event, event.currentTarget as any);
    }

    private segPanelFor(seg: 'empresa' | 'sede' | 'estacion'): Popover {
        return seg === 'empresa' ? this.segPanelEmpresa : seg === 'sede' ? this.segPanelSede : this.segPanelEstacion;
    }

    private hideSegPanels(except?: 'empresa' | 'sede' | 'estacion') {
        (['empresa', 'sede', 'estacion'] as const)
            .filter(seg => seg !== except)
            .forEach(seg => this.segPanelFor(seg)?.hide());
    }

    get segTitle() {
        return this.segMode === 'empresa' ? 'Empresa' : this.segMode === 'sede' ? 'Sede / Punto de venta' : 'Estación';
    }

    get segOptions(): any[] {
        return this.segMode === 'empresa' ? this.attachedCompanies : this.segMode === 'sede' ? this.offices : this.stations;
    }

    segLabel(item: any) {
        return this.segMode === 'empresa' ? item?.razonSocial : item?.nombre;
    }

    isCurrentSegItem(item: any) {
        const actual = this.segMode === 'empresa' ? this.companySelected : this.segMode === 'sede' ? this.office : this.saleStation;
        return this.segMode === 'empresa' ? this.mismaEmpresa(item, actual) : item?.id === actual?.id;
    }

    async selectSegItem(item: any) {
        if (!this.esCambio(item) || !(await this.confirmarCambio())) {
            this.hideSegPanels();
            return;
        }
        if (this.segMode === 'empresa') {
            this.empresaSel = item;
            this.onEmpresaChange();
        } else if (this.segMode === 'sede') {
            this.officeSel = item;
            this.onOfficeChange();
        } else {
            this.stationSel = item;
            this.dispatchStation();
        }
        this.hideSegPanels();
    }

    private mismaEmpresa(a: Company, b: Company): boolean {
        return a?.id === b?.id && String(a?.ruc ?? '') === String(b?.ruc ?? '');
    }

    private esCambio(item: any): boolean {
        return !this.isCurrentSegItem(item);
    }

    private syncWorkingValues() {
        this.empresaSel = this.attachedCompanies.find(c => this.mismaEmpresa(c, this.companySelected)) || this.companySelected;
        this.officeSel = this.offices.find(o => o?.id === this.office?.id) || this.office;
        this.stationSel = this.saleStation;
    }

    close() {
        this.sheetVisible = false;
        this.panelOpen = false;
        if (this.panel) {
            this.panel.hide();
        }
    }

    onPanelHide() {
        this.panelOpen = false;
    }

    async onEmpresaChangeConfirmado() {
        if (await this.confirmarCambio()) { this.onEmpresaChange(); }
    }

    async onOfficeChangeConfirmado() {
        if (await this.confirmarCambio()) { this.onOfficeChange(); }
    }

    async onStationChangeConfirmado() {
        if (await this.confirmarCambio()) { this.onStationChange(); }
    }

    onEmpresaChange() {
        this.dispatchCompany();
        const officeAsignado = this.offices.find(o => o.rucAsignado === this.empresaSel?.ruc);
        if (officeAsignado) {
            this.officeSel = officeAsignado;
            this.dispatchOffice();
            this.loadStations(this.officeSel, null, true);
        }
    }

    onOfficeChange() {
        // espejo del acople existente: la sede con RUC asignado arrastra la empresa
        if (this.officeSel?.rucAsignado) {
            const empresa = this.attachedCompanies.find(ea => ea.ruc === this.officeSel.rucAsignado);
            if (empresa) {
                this.empresaSel = empresa;
                this.dispatchCompany();
            }
        }
        this.dispatchOffice();
        this.loadStations(this.officeSel, null, true);
    }

    onStationChange() {
        this.dispatchStation();
    }

    private dispatchCompany() {
        if (this.empresaSel && !this.mismaEmpresa(this.empresaSel, this.companySelected)) {
            this.store.dispatch(new SetCompanySelected(this.empresaSel));
        }
    }

    private dispatchOffice() {
        if (this.officeSel && this.officeSel.id !== this.office?.id) {
            this.store.dispatch(new SetOffice({office: this.officeSel}));
        }
    }

    private dispatchStation() {
        if (this.stationSel && this.stationSel.id !== this.saleStation?.id) {
            this.store.dispatch(new SetSaleStation({saleStation: this.stationSel}));
        }
    }

    private initWorkingValues() {
        this.empresaSel = this.attachedCompanies.find(c => this.mismaEmpresa(c, this.companySelected)) || this.companySelected;
        this.officeSel = this.offices.find(o => o?.id === this.office?.id) || this.office;
        this.loadStations(this.officeSel, this.saleStation, false);
    }

    private loadStations(office: Office, keepCurrent: SaleStation, dispatchOnLoad: boolean) {
        this.stations = [];
        this.stationSel = null;
        this.stationsForOfficeId = office?.id ?? null;
        if (!office) {
            return;
        }
        this.loadingStations = true;
        const consulta = ++this.consultaEstaciones;
        const params = [
            `paginacion=${false}`,
            `idPuntoVenta=${office.id}`
        ];
        this.saleStationService.findAllList(params.join('&'))
            .subscribe(data => {
                // Una respuesta de otra sede (cambio A→B con A más lenta) no debe despachar su estación.
                if (consulta !== this.consultaEstaciones || this.stationsForOfficeId !== (office?.id ?? null)) { return; }
                const estacionFija = this.loginService.getUser().estacionVenta;
                this.stations = estacionFija ? data.filter(ev => ev.id === estacionFija.id) : data;
                this.stationSel = (keepCurrent && this.stations.find(s => s.id === keepCurrent.id)) || this.stations[0];
                this.loadingStations = false;
                if (dispatchOnLoad) {
                    this.dispatchStation();
                }
            }, () => {
                if (consulta === this.consultaEstaciones) { this.loadingStations = false; }
            });
    }

}
